import { useRef, type ReactNode } from "react";
import { CheckCircle2, Download, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useScrollSpotlight } from "../scroll-spotlight";

import { CopyableTerminal } from "../copyable-terminal";

/* -------------------------------------------------------------------------- */
/*  Shared primitives                                                         */
/* -------------------------------------------------------------------------- */

function NumberedInstruction({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex flex-col items-center">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
          {step}
        </div>
        <div className="mt-3 w-px flex-1 bg-white/[0.06]" />
      </div>
      <div className="min-w-0 flex-1 pb-28">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function NumberedInstructionLast({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollSpotlight(ref);

  return (
    <div ref={ref} className="flex gap-5 transition-opacity duration-500 ease-out">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.05] text-sm font-semibold text-foreground">
        {step}
      </div>
      <div className="min-w-0 flex-1 pb-12">
        <p className="mt-1 text-lg font-semibold text-foreground">{title}</p>
        <div className="mt-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}


function InfoCard({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-5">
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Install GitHub CLI step                                                   */
/* -------------------------------------------------------------------------- */

export function InstallGHStep() {
  const { selectedPlatform } = useOnboarding();
  const platform = selectedPlatform ?? "mac";
  const isMac = platform === "mac";

  return (
    <div className="space-y-0">
      <NumberedInstruction step={1} title="What is GitHub CLI?">
        <p className="text-sm leading-6 text-muted-foreground">
          GitHub CLI (<code className="rounded bg-white/[0.08] px-1.5 py-0.5 font-mono text-xs">gh</code>) is the official GitHub command-line app. SpecTree uses it to sign you in and download private packages. Without it, the next setup steps will not work.
        </p>
        <InfoCard>
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] bg-blue-500/10 text-blue-400">
              <Download className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-foreground">GitHub CLI</p>
                <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  Latest
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {isMac
                  ? 'Download it from cli.github.com, or run "brew install gh" if you already use Homebrew.'
                  : "Download the Windows installer from cli.github.com and follow the setup screens."}
              </p>
            </div>
          </div>
        </InfoCard>
      </NumberedInstruction>

      <NumberedInstruction step={2} title="Install GitHub CLI">
        <p className="text-sm leading-6 text-muted-foreground">
          {isMac
            ? "The quickest way on a Mac is Homebrew. If you do not use Homebrew, download the Mac installer from the website instead."
            : "Download the Windows installer (.msi), run it, and keep the default options. Then close and reopen your terminal."}
        </p>
        {isMac && (
          <InfoCard>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
              Homebrew (optional)
            </p>
            <CopyableTerminal title="Terminal" copyText="brew install gh">
              <p><span className="text-foreground/45">$ </span>brew install gh</p>
            </CopyableTerminal>
          </InfoCard>
        )}
        <a
          href="https://cli.github.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.05] px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-white/[0.1]"
        >
          Open cli.github.com
          <ExternalLink className="h-4 w-4" />
        </a>
      </NumberedInstruction>

      <NumberedInstructionLast step={3} title="Verify the installation">
        <p className="text-sm leading-6 text-muted-foreground">
          Open your terminal and run the command below. You should see a version number. If the command is not found, close the terminal, open it again, and try one more time.
        </p>
        <CopyableTerminal title="Run this command" copyText="gh --version">
          <div className="space-y-1.5">
            <p>
              <span className="text-foreground/45">$ </span>
              <span className="text-foreground">gh --version</span>
            </p>
            <p className="text-emerald-400">gh version 2.62.0 (2024-12-05)</p>
          </div>
        </CopyableTerminal>
        <InfoCard>
          <div className="flex items-start gap-3">
            <CheckCircle2 className={cn("mt-0.5 h-4 w-4 shrink-0 text-emerald-400")} />
            <p className="text-sm leading-6 text-muted-foreground">
              If the command prints a version number, you are ready. The exact version can be different.
            </p>
          </div>
        </InfoCard>
      </NumberedInstructionLast>
    </div>
  );
}
